import AddIcon from "@mui/icons-material/Add";
import CloseIcon from '@mui/icons-material/Close';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SearchIcon from '@mui/icons-material/Search';
import TuneIcon from '@mui/icons-material/Tune';
import {
  Box,
  Button,
  Checkbox,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography
} from '@mui/material';
import Menu from '@mui/material/Menu';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '@/store/hooks';
import { selectSelectedSiteName, selectSelectedSiteId, selectUserId } from '@/store/selectors';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useTranslation } from 'react-i18next';
import { usePermissions } from '../../../hooks/usePermissions';
import DeviceService from '../../../services/devices';
import { useAuth0 } from '@auth0/auth0-react';
import AddTurnAroundModal from '../../../components/turnaround/AddTurnAroundModal';
import { CustomCheckboxIcon, CustomCheckboxIconChecked } from '../../common/custom-checkbox';
import ConfirmationModal from "@/components/common/modals/ConfirmationModal";
import MessageModal from "@/components/common/modals/MessageModal";
import { Turnaround } from "@/types/turnaround";
import { formatDate } from "@/utils/date";


const DEVICE_STATUS = [
  { key: 'decommissioned', labelKey: 'decommissioned' },
  { key: 'is_critical', labelKey: 'critical' },
  { key: 'site_verified', labelKey: 'siteVerified' }
];

interface TurnAroundDetailHeaderProps {
  filterDevices?: (filters: any) => void;
  paginationModel: { page: number; pageSize: number };
  pageSizeOptions?: number[];
  onPaginationModelChange?: (model: { page: number; pageSize: number }, details?: any) => void;
  refreshTurnAroundDetails: () => void;
  turnaroundData: Turnaround | null;
}

const TurnAroundDetailHeader = React.memo(({
  filterDevices,
  paginationModel,
  pageSizeOptions = [10, 25, 50, 75, 100],
  onPaginationModelChange,
  refreshTurnAroundDetails,
  turnaroundData,
}: TurnAroundDetailHeaderProps) => {
  const [actionsAnchorEl, setActionsAnchorEl] = useState<null | HTMLElement>(null);
  const actionsMenuOpen = Boolean(actionsAnchorEl);
  const handleActionsClick = (event: React.MouseEvent<HTMLElement>) => {
    setActionsAnchorEl(event.currentTarget);
  };
  const handleActionsClose = () => {
    setActionsAnchorEl(null);
  };
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { getFeatureActions } = usePermissions();
  const { getAccessTokenSilently } = useAuth0();

  const selectedSiteId = useAppSelector(selectSelectedSiteId);
  const siteName = useAppSelector(selectSelectedSiteName);
  const deviceCount = turnaroundData?.device_count || 0;

  const showgdmnumber = getFeatureActions('Navigation', 'View Customer GDM ID', 'Customer Navigation');
  // Get turnaround detail from prop
  const turnAroundDetail = turnaroundData;
  const userId = useAppSelector(selectUserId);
  
  // Filters state (copied from DeviceListHeader)
  const [search, setSearch] = useState('');
  const [showDeviceType, setShowDeviceType] = useState(false);
  const [showDeviceStatus, setShowDeviceStatus] = useState(false);
  const [selectedDeviceTypes, setSelectedDeviceTypes] = useState<string[]>([]);
  const [selectedDeviceStatus, setSelectedDeviceStatus] = useState<string[]>([]);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [filterCount, setFilterCount] = useState(0);
  const [deviceTypes, setDeviceTypes] = useState<string[]>([]);
  const [filtersChanged, setFiltersChanged] = useState(false);
  // Optionally add more filter states as needed

  // State for Edit TurnAround modal
  const [editTurnaroundModalOpen, setEditTurnaroundModalOpen] = useState(false);
  const [editingTurnaround, setEditingTurnaround] = useState<any>(null);

  // State for Get Parts and Accessories
  const [openPartsConfirmDialog, setOpenPartsConfirmDialog] = useState(false);
  const [showPartsSuccessDialog, setShowPartsSuccessDialog] = useState(false);

  useEffect(() => {
    const fetchDeviceTypes = async () => {
      try {
        const accessToken = await getAccessTokenSilently();
        const deviceService = new DeviceService(undefined, accessToken);
        const res = await deviceService.getTypes();
        const items = res?.results.map((item: any) => item.name);
        setDeviceTypes(items);
      } catch {
        setDeviceTypes([]);
      }
    };
    fetchDeviceTypes();
  }, [getAccessTokenSilently]);

  // Debounce filter update on search
  const debounceTimeout = useRef<NodeJS.Timeout | null>(null);
  useEffect(() => {
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }
    debounceTimeout.current = setTimeout(() => {
      triggerFilterUpdate();
    }, 400);
    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
  }, [search]);

  useEffect(() => {
    if (selectedDeviceStatus.length > 0 || selectedDeviceTypes.length > 0) {
      setFiltersChanged(true);
    }
    else {
      setFiltersChanged(false);
    }
  }, [selectedDeviceStatus, selectedDeviceTypes, deviceTypes]);

  const triggerFilterUpdate = () => {
    if (filterDevices) {
      const filters = {
        search,
        device_type: selectedDeviceTypes,
        device_state: selectedDeviceStatus.toString(),
      };
      filterDevices(filters);
      setFiltersChanged(false);
    }
    updateAndLogFilters();
  };

  useEffect(() => {
    // Update filter count based on selected filters
    const count = selectedDeviceTypes.length + selectedDeviceStatus.length;
    setFilterCount(count);
  }, [selectedDeviceStatus, selectedDeviceTypes]);

  // Helper for updating and logging filter object
  const updateAndLogFilters = () => {
    setShowDeviceType(false);
    setShowDeviceStatus(false);
  };

  // Handlers for multi-select
  const handleDeviceTypeChange = (type: string, checked: boolean) => {
    setSelectedDeviceTypes(prev => {
      const updated = checked
        ? [...prev, type]
        : prev.filter(t => t !== type);
      return updated;
    });
  };

  const clearFilters = () => {
    if (selectedDeviceStatus.length === 0 && selectedDeviceTypes.length === 0) {
      return; // No filters to clear
    }
    setSelectedDeviceTypes([]);
    setSelectedDeviceStatus([]);
    updateAndLogFilters();
    triggerFilterUpdate();
    if (filterDevices) {
      filterDevices(null);
    }
  };

  const formatLabel = (status: string) => {
    return status
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  };

  const handleDeviceStatusChange = (status: string | null) => {
    setSelectedDeviceStatus(status ? [status] : []);
    setFiltersChanged(!!status);
    setShowDeviceStatus(false);
  };

  // Handler for removing chip
  const handleDeleteChip = (type: 'type' | 'status', value: string) => {
    if (type === 'type') {
      setSelectedDeviceTypes(prev => {
        const updated = prev.filter(t => t !== value);
        if (filterDevices) {
          const filters = {
            search,
            device_type: updated,
            device_state: selectedDeviceStatus.toString(),
          };
          filterDevices(filters);
        }
        return updated;
      });
      setFiltersChanged(false);
    }
    if (type === 'status') {
      setSelectedDeviceStatus([]);
      setFiltersChanged(false);
      if (filterDevices) {
        const filters = {
          search,
          device_type: selectedDeviceTypes,
          device_state: '',
        };
        filterDevices(filters);
      }
    }
  };

  // Handler for Edit TurnArounds menu item
  const handleEditTurnaroundClick = () => {
    handleActionsClose();
    setEditingTurnaround(turnAroundDetail);
    setEditTurnaroundModalOpen(true);
  };

  // Handler for Get Parts and Accessories
  const handleGetPartsClick = () => {
    handleActionsClose();
    setOpenPartsConfirmDialog(true);
  };

  const handleGetPartsAndAccessories = async () => {
    setOpenPartsConfirmDialog(false);
    setShowPartsSuccessDialog(true);

    // Get device IDs from turnaround data
    const deviceIds = turnaroundData?.devices?.map(d => d.device_id) || [];

    const params = {
      user_id: userId,
      device_ids: deviceIds,
      report_type: "turnaround",
      report_data: turnAroundDetail?.name,
    };

    try {
      const accessToken = await getAccessTokenSilently();
      const deviceService = new DeviceService(undefined, accessToken);
      await deviceService.generateEspirReport(params);
      console.log("Parts and accessories request submitted successfully");
    } catch (err) {
      console.error("Parts and accessories request failed:", err);
    }
  };

  // Actions menu items as array
  const actionsMenuItems = [
    {
      label: t('editTurnaround', 'Edit Turnaround'),
      onClick: handleEditTurnaroundClick,
    },
  ];

  const handleAddDevice = () => {
    navigate('/devices');
  }


  return (
    <Box>
      <Box className="device-header-container">
        <Box>
          <Typography variant="h3" className="page-title">
            {turnAroundDetail?.name}
          </Typography>
          <Box className="flex-start" sx={{ mt: 2, mb: 1 }}>
            <Typography variant="subtitle2" className="site-label">
              {t("TurnAroundDate", "Turnaround Date:")}
            </Typography>
            <Typography variant="body2" className="site-value">
              {turnAroundDetail && turnAroundDetail.start_date && turnAroundDetail.end_date
                ? `${formatDate(turnAroundDetail.start_date, 'MMM DD')} - ${formatDate(turnAroundDetail.end_date, 'MMM DD, YYYY')}`
                : "-"}
            </Typography>
          </Box>
          <Box className="flex-start">
            <Typography variant="subtitle2" className="site-label">
              {t("site", "Site:")}
            </Typography>
            <Typography variant="body2" className="site-value">
              {siteName ? (
                <>
                  <span className="siteName">{siteName}</span>
                  {selectedSiteId && showgdmnumber?.canView && (
                    <>
                      <span className="pipe">|</span>
                      <span className="siteName">
                        {selectedSiteId}
                      </span>
                    </>
                  )}
                </>
              ) : (
                t("noSiteSelected", "No Site Selected")
              )}
            </Typography>
          </Box>
        </Box>
        <Box>
          <Button
            startIcon={<AddIcon />}
            variant="text"
            className="add-device-button text-uppercase"
            onClick={handleAddDevice}
          >
            {t('addDeviceToScope', 'Add Device to Scope')}
          </Button>
          <Button
            endIcon={<MoreVertIcon />}
            variant="text"
            className="add-device-button"
            onClick={handleActionsClick}
          >
            {t('actions', 'ACTIONS')}
          </Button>
          <Menu
            anchorEl={actionsAnchorEl}
            open={actionsMenuOpen}
            onClose={handleActionsClose}
            disableScrollLock={true}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            {actionsMenuItems.map((item, idx) => (
              <MenuItem key={idx} onClick={item.onClick}>{item.label}</MenuItem>
            ))}
          </Menu>
        </Box>
      </Box>
      <Box className="search-controls-container">
        <Box className="search-input-container flex-start">
          <TextField
            variant="outlined"
            placeholder={t('turnAroundDetailSearchPlaceholder', 'Search by Tag, Serial, Type')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="search-input"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />
        </Box>
        <Typography
          variant="body2"
          className="common-data-grid-header-row-count"
        >
          {paginationModel.page * paginationModel.pageSize + 1}
          - {Math.min((paginationModel.page + 1) * paginationModel.pageSize, deviceCount)}
          {" of"} {deviceCount} {t('devices', 'Devices')}
        </Typography>
        {/* Removed row count display - will be shown in grid component if needed */}
        <Box className="filter-controls-container">

          {/* Per Page Dropdown (like CommonDataGrid) */}
          <Box className="common-data-grid-header-right" sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Typography className="common-data-grid-header-right-label-show hide-mobile" sx={{ fontWeight: 500 }}>
              {t("show", "Show:")}
            </Typography>
            <FormControl size="small" className="common-data-grid-header-right-formcontrol">
              <Select
                value={paginationModel?.pageSize || 10}
                onChange={e => {
                  if (onPaginationModelChange) {

                    onPaginationModelChange({ ...paginationModel, pageSize: e.target.value as number, page: 0 }, { reason: "pageSizeChange" });
                  }
                }}
                displayEmpty
                className="common-data-grid-header-right-select"
                inputProps={{ "aria-label": "Show Per Page" }}
              >
                {pageSizeOptions.map((size) => (
                  <MenuItem key={size} value={size} className="common-data-grid-header-right-menuitem">
                    {size} {t("perPage", "PER PAGE")}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box className="filters-toggle">
            <Typography
              variant="body1"
              className="filters-label"
              onClick={() => setFiltersOpen((open) => !open)}
            >
              {t('filters', 'Filters')} {filterCount ? `(${filterCount})` : null}
            </Typography>
            <IconButton
              className="filters-icon-button"
              onClick={() => setFiltersOpen((open) => !open)}
              aria-label={filtersOpen ? 'Close Filters' : 'Open Filters'}
            >
              {filtersOpen ? <CloseIcon /> : <TuneIcon />}
            </IconButton>
          </Box>
        </Box>
      </Box>
      {filtersOpen && (
        <>
          <Box className="filter-chips-container">
            <Typography className="filter-by-label">{t('filterBy', 'Filter by:')}</Typography>
            {selectedDeviceTypes.map((type) => (
              <Box key={type} className="filter-chip">
                <Typography className="filter-chip-text">{type}</Typography>
                <IconButton
                  size="small"
                  onClick={() => handleDeleteChip('type', type)}
                  className="filter-chip-close"
                  aria-label={`Remove ${type}`}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
            {selectedDeviceStatus.map((statusKey) => {
              const statusObj = DEVICE_STATUS.find(s => s.key === statusKey);
              return (
                <Box key={statusKey} className="filter-chip">
                  <Typography className="filter-chip-text">{statusObj ? t(statusObj.labelKey, statusKey) : formatLabel(statusKey)}</Typography>
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteChip('status', statusKey)}
                    className="filter-chip-close"
                    aria-label={`Remove ${statusKey}`}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              );
            })}
            {filterCount > 0 &&
              <Box className="clear-filters-desktop">
                <Typography
                  className={`clear-filters-text ${filterCount > 0 ? 'clear-filter-text-enable' : ''}`}
                  onClick={() => clearFilters()}
                >
                  {t('clearAllFilters', 'Clear All')}
                </Typography>
              </Box>}
          </Box>
          <Box className="filter-dropdowns-container">
            {/* Device Type Dropdown */}
            <Box className="filter-dropdown">
              {!showDeviceType ? (
                <Box className="filter-dropdown-collapsed"
                  onClick={() => { setShowDeviceType(true); setShowDeviceStatus(false); }}
                  style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                >
                  {t('deviceType', 'DEVICE TYPE')} ({selectedDeviceTypes.length})
                  <ExpandMoreIcon style={{ marginLeft: 4 }} />
                </Box>
              ) : (
                <Paper elevation={6} className="filter-dropdown-expanded">
                  <Box className="filter-dropdown-header"
                    onClick={() => setShowDeviceType(false)}
                    style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                  >
                    {t('deviceType', 'DEVICE TYPE')} ({selectedDeviceTypes.length})
                    <ExpandLessIcon style={{ marginLeft: 4 }} />
                  </Box>
                  <Box className="filter-dropdown-content">
                    <FormGroup>
                      {deviceTypes.map((type) => (
                        <FormControlLabel
                          key={type}
                          control={
                            <Checkbox
                              checked={selectedDeviceTypes.includes(type)}
                              onChange={(e) => handleDeviceTypeChange(type, e.target.checked)}
                              icon={<CustomCheckboxIcon />}
                              checkedIcon={<CustomCheckboxIconChecked tickColor='green' />}
                            />
                          }
                          label={type}
                        />
                      ))}
                    </FormGroup>
                  </Box>
                </Paper>
              )}
            </Box>
            {/* Device Status Dropdown */}
            <Box className="filter-dropdown">
              {!showDeviceStatus ? (
                <Box
                  className="filter-dropdown-collapsed"
                  onClick={() => {
                    setShowDeviceStatus(true);
                    setShowDeviceType(false);
                  }}
                  style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                >
                  {t('deviceStatus', 'DEVICE STATUS')} ({selectedDeviceStatus.length})
                  <ExpandMoreIcon style={{ marginLeft: 4 }} />
                </Box>
              ) : (
                <Paper elevation={6} className="filter-dropdown-expanded">
                  <Box className="filter-dropdown-header"
                    onClick={() => setShowDeviceStatus(false)}
                    style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
                  >
                    {t('deviceStatus', 'DEVICE STATUS')} ({selectedDeviceStatus.length})
                    <ExpandLessIcon style={{ marginLeft: 4 }} />
                  </Box>
                  <Box className="filter-dropdown-content">
                    {DEVICE_STATUS.map((status) => (
                      <Box
                        key={status.key}
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "center",
                          padding: "8px 16px",
                          cursor: "pointer",
                          backgroundColor: selectedDeviceStatus.includes(status.key)
                            ? "#f0f7ff"
                            : "transparent",
                        }}
                        onClick={() => {
                          if (selectedDeviceStatus.includes(status.key)) {
                            setSelectedDeviceStatus([]);
                          } else {
                            setSelectedDeviceStatus([status.key]);
                          }
                          setFiltersChanged(true);
                          setShowDeviceStatus(false);
                        }}
                      >
                        <Typography
                          sx={{
                            fontWeight: selectedDeviceStatus.includes(status.key)
                              ? "bold"
                              : "normal",
                          }}
                        >
                          {t(status.labelKey, status.key)}
                        </Typography>
                        {selectedDeviceStatus.includes(status.key) && (
                          <span style={{ color: "green", fontWeight: "bold" }}>✓</span>
                        )}
                      </Box>
                    ))}
                  </Box>
                </Paper>
              )}
            </Box>
            <Box className="apply-filters-desktop">
              <Button
                variant="contained"
                className={`apply-filters-button ${filtersChanged ? 'active' : 'inactive'}`}
                onClick={() => triggerFilterUpdate()}
                disabled={!filtersChanged}
              >
                {t('applyFilters', 'APPLY FILTERS')}
              </Button>
            </Box>
          </Box>
          <Box className="apply-filters-mobile-container">
            <Button
              variant="contained"
              className="apply-filters-mobile"
              onClick={() => triggerFilterUpdate()}
            >
              {t('applyFilters', 'APPLY FILTERS')}
            </Button>
          </Box>
          {filterCount > 0 &&
            <Box className="clear-filters-mobile-container">
              <Button
                variant="outlined"
                className="clear-filters-mobile"
                onClick={() => clearFilters()}
              >
                {t('clearAllFilters', 'Clear All')}
              </Button>
            </Box>}
        </>
      )}
      <Divider className="header-divider" />

      {/* Edit TurnAround Modal */}
      {editTurnaroundModalOpen && (
        <AddTurnAroundModal
          onClose={() => {
            setEditTurnaroundModalOpen(false);
            setEditingTurnaround(null);
          }}
          onSuccess={() => {
            setEditTurnaroundModalOpen(false);
            setEditingTurnaround(null);
            refreshTurnAroundDetails();
          }}
          existingTurnaround={editingTurnaround}
        />
      )}

      {/* Parts and Accessories Confirmation Dialog */}
      <ConfirmationModal
        open={openPartsConfirmDialog}
        onClose={() => setOpenPartsConfirmDialog(false)}
        onConfirm={handleGetPartsAndAccessories}
        title={t("getPartsAndAccessories", "Get Parts and Accessories")}
        message={t("partsAccessoriesConfirmMessage", "Parts and accessories will be fetched for the selected device(s). You will receive an email notification when the task is completed.")}
        confirmText={t("confirm", "Confirm")}
        cancelText={t("cancel", "Cancel")}
      />

      {/* Parts and Accessories Success Dialog */}
      <MessageModal
        open={showPartsSuccessDialog}
        onClose={() => setShowPartsSuccessDialog(false)}
        title={t("partsAccessoriesRequested", "Parts and Accessories Requested")}
        message={t("partsAccessoriesSuccessMessage", "Your request has been submitted successfully. Parts and accessories data will be processed, and you will receive an email notification once completed.")}
        buttonText={t("close", "Close")}
        onButtonClick={() => setShowPartsSuccessDialog(false)}
        showButton={true}
      />
    </Box>
  );
});

export default TurnAroundDetailHeader;
